import { forwardRef } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import {
  BORDER_RADIUS_BUTTON,
  FONT_WEIGHT_BUTTON,
} from "../../utils/constants";
import {
  BOX_SHADOW_FIELDS,
  INPUT_COLOR_BORDER,
  PILL_TRIGGER_BORDER_ACTIVE_COLOR,
  PILL_TRIGGER_BORDER_HOVER_COLOR,
  PILL_TRIGGER_FOCUS_RING,
  PILL_TRIGGER_HOVER_BACKGROUND_COLOR,
  SECONDARY_COLOR_TEXT,
} from "../../utils/colors";

//eslint-disable-next-line
export const PILL_TRIGGER_VARIANT = {
  PILL: "pill",
  PLAIN: "plain",
};

// MUI medium Button renders at 36.5px (campaigns header primary action):
// 6px padding + 14px font * 1.75 line height + 6px padding.
const PILL_TRIGGER_HEIGHT = 36.5;
const PILL_TRIGGER_FONT_SIZE = 13;

const PILL_SX = {
  borderRadius: BORDER_RADIUS_BUTTON,
  border: `1px solid ${INPUT_COLOR_BORDER}`,
  backgroundColor: "background.paper",
  boxSizing: "border-box",
  height: PILL_TRIGGER_HEIGHT,
  minWidth: 0,
  pl: 1.75,
  pr: 1.25,
  gap: 0.75,
  justifyContent: "flex-start",
  boxShadow: BOX_SHADOW_FIELDS,
  transition:
    "border-color 150ms ease, background-color 150ms ease, box-shadow 150ms ease",
  "&:hover": {
    borderColor: PILL_TRIGGER_BORDER_HOVER_COLOR,
    backgroundColor: PILL_TRIGGER_HOVER_BACKGROUND_COLOR,
  },
  "&:focus-visible": {
    outline: "none",
    borderColor: PILL_TRIGGER_BORDER_ACTIVE_COLOR,
    boxShadow: `${BOX_SHADOW_FIELDS}, ${PILL_TRIGGER_FOCUS_RING}`,
  },
};

const PILL_ACTIVE_SX = {
  borderColor: PILL_TRIGGER_BORDER_ACTIVE_COLOR,
  backgroundColor: PILL_TRIGGER_HOVER_BACKGROUND_COLOR,
};

const PILL_DISABLED_SX = {
  opacity: 0.5,
  cursor: "not-allowed",
  "&:hover": {
    borderColor: INPUT_COLOR_BORDER,
    backgroundColor: "background.paper",
  },
};

const PILL_TEXT_SX = {
  fontSize: PILL_TRIGGER_FONT_SIZE,
  lineHeight: 1,
  fontWeight: FONT_WEIGHT_BUTTON,
  letterSpacing: "-0.01em",
  color: "text.primary",
};

const PILL_ICON_BOX_SX = {
  width: 16,
  height: 16,
  // MuiSvgIcon theme sets color with !important. A palette path cannot
  // carry !important, so the secondary text color is applied directly.
  "& .MuiSvgIcon-root": {
    fontSize: 16,
    color: `${SECONDARY_COLOR_TEXT} !important`,
  },
};

const PILL_CHEVRON_SX = {
  fontSize: 18,
  color: `${SECONDARY_COLOR_TEXT} !important`,
  flexShrink: 0,
  transition: "transform 150ms",
};

/**
 * Shared visual trigger (icon + text + chevron) for filters.
 * It only handles appearance. Click behavior (menu, popover, modal)
 * is defined by the caller through `onClick`.
 *
 * - variant "pill": full style (border, shadow, fixed height and spacing).
 *   From the outside, only width or external margins should be adjusted via `sx`.
 * - variant "plain": no styles of its own. The caller defines the style.
 *
 * `isActive` is the open state. On the pill variant it rotates the chevron,
 * applies the active border and sets `aria-expanded`.
 */
const PillTriggerButton = forwardRef(
  (
    {
      text,
      startIcon = null,
      showChevron = true,
      disabled = false,
      isActive = false,
      onClick,
      variant = PILL_TRIGGER_VARIANT.PLAIN,
      sx = {},
      sxText = {},
      ...otherProps
    },
    ref
  ) => {
    const isPill = variant === PILL_TRIGGER_VARIANT.PILL;
    const isInteractive = isPill && !disabled;

    const onKeyDownPill = (event) => {
      if (!isInteractive) return;
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      onClick?.(event);
    };

    return (
      <Box
        ref={ref}
        onClick={disabled ? undefined : onClick}
        {...(isPill
          ? {
              role: "button",
              tabIndex: disabled ? -1 : 0,
              "aria-haspopup": "true",
              "aria-expanded": isActive,
              "aria-disabled": disabled,
              onKeyDown: onKeyDownPill,
            }
          : {})}
        sx={{
          cursor: disabled ? "default" : "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          ...(isPill ? PILL_SX : {}),
          ...(isPill && isActive ? PILL_ACTIVE_SX : {}),
          ...(isPill && disabled ? PILL_DISABLED_SX : {}),
          ...sx,
        }}
        {...otherProps}
      >
        {startIcon && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              ...(isPill ? {} : { mr: 1 }),
              lineHeight: 0,
              ...(isPill ? PILL_ICON_BOX_SX : {}),
            }}
          >
            {startIcon}
          </Box>
        )}
        <Typography
          noWrap
          sx={{
            ...(isPill ? { flex: 1, minWidth: 0, ...PILL_TEXT_SX } : {}),
            ...sxText,
          }}
        >
          {text}
        </Typography>
        {showChevron &&
          (isPill ? (
            <KeyboardArrowDownRoundedIcon
              sx={{
                ...PILL_CHEVRON_SX,
                ...(isActive ? { transform: "rotate(180deg)" } : {}),
              }}
            />
          ) : (
            <KeyboardArrowDownIcon />
          ))}
      </Box>
    );
  }
);

PillTriggerButton.displayName = "PillTriggerButton";

export default PillTriggerButton;
